import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { FormDialog } from '@components/molecules/FormDialog'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { i18nValidation, scopedTranslation } from '@frontend/utils/I18n'
import { useSkipSupporter } from '../../hooks/useSkipSupporter'
import { clsx } from 'clsx'
import dayjs from 'dayjs'
import type { SkipSupporterDialogState } from '../../../supporters-management/types/dialogTypes'

const t = scopedTranslation('components.skip_supporter_dialog')

type SkipSupporterDialogProps = {
  onClose: () => void
  dialogState: SkipSupporterDialogState
}

type SkipSelection =
  | { type: 'duration'; minutes: number }
  | { type: 'this_cycle' }
  | { type: 'today' }
  | { type: 'until_date' }
  | { type: 'permanent' }

type FormData = {
  reason: string
}

const schema = yup.object({
  reason: yup
    .string()
    .label(t('reason_label'))
    .default('')
})

const QUICK_DURATIONS = [
  { minutes: 15, label: 'durations.15_min' },
  { minutes: 30, label: 'durations.30_min' },
  { minutes: 60, label: 'durations.1_hour' },
  { minutes: 120, label: 'durations.2_hours' },
] as const

const RADIO_OPTIONS = ['this_cycle', 'today', 'until_date', 'permanent'] as const
type RadioOptionType = typeof RADIO_OPTIONS[number]

const chipBase = 'tw:px-3 tw:py-2.5 tw:rounded-lg tw:border tw:text-center tw:text-sm tw:font-medium tw:cursor-pointer tw:transition-colors'
const chipDefault = 'tw:border-gray-200 tw:text-gray-900 tw:hover:border-primary-300 tw:hover:bg-primary-50'
const chipSelected = 'tw:border-primary-500 tw:bg-primary-50 tw:text-primary-700'

const RADIO_OPTION_CONFIG: Record<RadioOptionType, { labelKey: string; hintKey: string }> = {
  this_cycle: { labelKey: 'skip_for_this_cycle', hintKey: 'skip_for_this_cycle_hint' },
  today: { labelKey: 'skip_for_today', hintKey: 'skip_for_today_hint' },
  until_date: { labelKey: 'skip_until', hintKey: 'skip_until_hint' },
  permanent: { labelKey: 'remove_from_session', hintKey: 'remove_from_session_hint' },
}

export function SkipSupporterDialog({ onClose, dialogState }: SkipSupporterDialogProps) {
  const { callingSessionSupporterId, supporterName } = dialogState.dialogData
  const [selection, setSelection] = useState<SkipSelection | null>(null)
  const [customDate, setCustomDate] = useState('')

  const { skip, isLoading } = useSkipSupporter({
    alertsPath: 'skipSupporter',
    reloadProps: ['callingSession', 'callingSessionSupporter'],
  })

  const isReasonRequired = selection?.type === 'permanent'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { reason: '' },
  })

  const onSubmit = async (data: FormData) => {
    if (!selection) return

    if (isReasonRequired && !data.reason.trim()) {
      setError('reason', { message: i18nValidation('required')({ label: t('reason_label'), path: 'reason' }) })
      return
    }

    let skipType: string
    let expiresAt: string | undefined

    switch (selection.type) {
      case 'duration':
        skipType = 'duration'
        expiresAt = new Date(Date.now() + selection.minutes * 60000).toISOString()
        break
      case 'this_cycle':
        skipType = 'this_cycle'
        break
      case 'today':
        skipType = 'today'
        break
      case 'until_date':
        skipType = 'until_date'
        expiresAt = customDate ? new Date(customDate).toISOString() : undefined
        break
      case 'permanent':
        skipType = 'permanent'
        break
    }

    const success = await skip({
      callingSessionSupporterId,
      supporterName,
      skipType,
      expiresAt,
      reason: data.reason.trim() || undefined,
    })

    if (success) onClose()
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    handleSubmit(onSubmit)(e)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onClose()
  }

  const handleDurationClick = (minutes: number) => {
    setSelection({ type: 'duration', minutes })
    clearErrors('reason')
  }

  const handleRadioChange = (value: string) => {
    setSelection({ type: value as RadioOptionType })
    if (value !== 'permanent') clearErrors('reason')
  }

  const radioValue = selection && selection.type !== 'duration' ? selection.type : ''
  const reasonError = errors.reason?.message

  const isValid = selection !== null
    && (selection.type !== 'until_date' || customDate !== '')

  return (
    <FormDialog.Root
      open={true}
      onOpenChange={handleOpenChange}
      testId="skip-supporter-dialog"
      preventOutsideClick
    >
      <FormDialog.Title alertsPath="skipSupporter">
        {t('title', { name: supporterName })}
      </FormDialog.Title>
      <FormDialog.Form onSubmit={handleFormSubmit}>
        <FormDialog.Body>
          <p className="tw:text-sm tw:text-gray-500">{t('description')}</p>

          <div>
            <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:pb-1">
              {t('call_back_later')}
            </label>
            <div className="tw:grid tw:grid-cols-4 tw:gap-2">
              {QUICK_DURATIONS.map(({ minutes, label }) => {
                const isSelected = selection?.type === 'duration' && selection.minutes === minutes
                return (
                  <button
                    key={minutes}
                    type="button"
                    onClick={() => handleDurationClick(minutes)}
                    className={clsx(chipBase, isSelected ? chipSelected : chipDefault)}
                  >
                    {t(label)}
                  </button>
                )
              })}
            </div>
          </div>

          <RadioGroupField<RadioOptionType>
            label={t('skip_for_longer')}
            appearance="cards"
            value={radioValue || null}
            onValueChange={handleRadioChange}
            options={RADIO_OPTIONS.map((option) => ({
              value: option,
              label: t(RADIO_OPTION_CONFIG[option].labelKey),
              description: t(RADIO_OPTION_CONFIG[option].hintKey),
              whenSelected: option === 'until_date' && (
                <div className="tw:pl-7 tw:pt-2 tw:pb-1">
                  <input
                    type="datetime-local"
                    aria-label={t('skip_until')}
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    className="tw:w-full tw:px-3 tw:py-2 tw:border tw:rounded-lg tw:border-gray-400 tw:text-sm tw:focus-within:border-primary-400 tw:focus-within:ring-4 tw:ring-primary-100 tw:focus:outline-hidden"
                    min={dayjs().format('YYYY-MM-DDTHH:mm')}
                  />
                </div>
              ),
            }))}
          />

          <div>
            <label
              htmlFor="skip-reason"
              className={clsx(
                'tw:block tw:text-sm tw:font-medium tw:pb-1',
                reasonError ? 'tw:text-red-500' : 'tw:text-gray-700'
              )}
            >
              {t('reason_label')}{' '}
              <span className="tw:text-gray-400 tw:font-normal">
                {isReasonRequired ? t('reason_required') : t('reason_optional')}
              </span>
            </label>
            <div
              className={clsx(
                'tw:flex tw:items-center',
                'tw:px-3 tw:py-2',
                'tw:border tw:rounded-lg',
                reasonError
                  ? 'tw:border-red-400 tw:focus-within:border-red-500 tw:focus-within:ring-4 tw:ring-red-200'
                  : 'tw:border-gray-400 tw:focus-within:border-primary-400 tw:focus-within:ring-4 tw:ring-primary-100',
              )}
            >
              <input
                id="skip-reason"
                type="text"
                {...register('reason')}
                placeholder={t('reason_placeholder')}
                className="tw:w-full tw:text-sm tw:placeholder-gray-500 tw:focus:outline-hidden"
              />
            </div>
            {reasonError && (
              <p className="tw:text-red-500 tw:font-light tw:text-sm tw:pt-1" role="status">
                {reasonError}
              </p>
            )}
          </div>
        </FormDialog.Body>
        <FormDialog.Footer
          submitText={t('confirm')}
          isLoading={isLoading}
          disabled={!isValid}
        />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
